import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { AttachmentRenameComponent } from '@src/suite/common/components/attachments-list/modal/rename/attachment-rename';
import { CreateNewTemplateComponent } from '@src/suite/common/components/create-new-template/create-new-template';
import { DeleteTopicStoriesComponent } from '@src/suite/common/components/delete-topic-stories/st-delete-topic-stories.component';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { ObjectPlaceholderCreateComponent } from '@src/suite/common/components/object-placeholder-create/object-placeholder-create';
import { ViewStateOrder } from '@src/suite/common/data-models/complex-types/view-state-order';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ClipboardTypes } from '@src/suite/common/enums/clipboard-types';
import { BLACK_LINE_GUID, OPTION_ALL } from '@src/suite/common/enums/constants';
import { AppName, StoryOutputType, TeamScope, TopicDeletionMode } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { AttachmentService } from '@src/suite/common/services/attachment-service';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { Convertor } from '@src/suite/common/services/convertor';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { StItemAttachToDialogComponent } from '@src/suite/common/st-item-attach-to-dialog/st-item-attach-to-dialog';
import {
    FullscreenActions,
    OpenFullscreenModalActions,
    selectCurrentRecord,
    selectFullscreenNewRecord,
    selectFullscreenRecordGuid,
    selectFullscreenRundownSelectedLayout,
    selectIsCurrentStoryStandalone,
} from '@src/suite/common/store/fullscreen';
import { selectFieldDefsDict, selectTitleSeparator } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectListNewGuids } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NavigationActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { selectCurrentApplication, selectSystemSingleViewStateItem } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import {
    selectCurrentGroupBy,
    selectCurrentSortBy,
    selectCurrentSortByOrder,
    selectCurrentViewState,
    selectCurrentViewStateType,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { selectGroupedSortedTopics } from '@src/suite/common/store/playbook/playbook.selector';
import { TIME_FIELD_SUFFIX } from '@src/suite/common/utils/global-tables.utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { ML2HtmlUtils, MOS_CONTENT_ID_PREFIX } from '@src/suite/common/utils/ml-2-html-utils';
import { extractAttachments } from '@src/suite/common/utils/mos-utlis';
import { getTopicFieldsToUpdate, isStoryInRundown } from '@src/suite/common/utils/record-utils';
import { checkForDuplicateSlugObs } from '@src/suite/common/utils/store-utils';
import { getTextCommandsFromText, Util } from '@src/suite/common/utils/util';
import { getDatabasesOptions, isSingleViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { LiveSearchService } from '@src/suite/live/services/live-search.service';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { RUNDOWN_PROPERTIES_DIALOG_ID } from '@src/suite/live/store/effects/live.effects';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { WastebinService } from '@src/suite/playbook/services/wastebin-service';
import { TemplatesService } from '@src/suite/templates/services/templates.service';
import { DateTime } from 'luxon';
import { catchError, concatMap, delay, exhaustMap, filter, forkJoin, from, map, mergeMap, Observable, of, reduce, switchMap, take, takeUntil, tap, throwError } from 'rxjs';

import { Path } from '../../data-models/complex-types/path';
import { ViewStateActions } from '../navigation/actions/viewstate.actions';
import { NotesActions } from '../notes/notes.actions';
import {
    AssignmentRecordActions,
    DiscoverRecordActions,
    NotesRecordActions,
    PublicationRecordActions,
    RecordActions,
    RecordHttpActions,
    RundownRecordActions,
    StoriesRecordActions,
    TopicRecordActions,
    UserRecordActions,
} from './records.actions';
import {
    selectAssignmentRecordsMap,
    selectAssignments,
    selectNoteTemplatesMap,
    selectRundownsWithStories,
    selectRundownTemplates,
    selectRundownTemplatesMap,
    selectStoryRecordsMap,
    selectStoryTemplatesMap,
    selectSubStoriesByParentGuid,
    selectTopicRecordByGuid,
    selectTopicRecordsMap,
    selectTopicTemplatesMap,
} from './records.selectors';

@Injectable()
export class RecordEffects {
    private playbookService = inject(PlaybookService);
    private playbookSearchService = inject(PlaybookSearchService);
    private liveSearchService = inject(LiveSearchService);
    private rundownService = inject(RundownService);
    private templatesService = inject(TemplatesService);
    private appService = inject(AppService);
    private attachmentService = inject(AttachmentService);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private modalService = inject(ModalService);
    private viewStateService: ViewStateService = inject(ViewStateService);
    private convertor = inject(Convertor);
    private basicContentService = inject(BasicContentService);
    private globalTablesService = inject(GlobalTablesService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private dialog = inject(MatDialog);
    private wastebinService = inject(WastebinService);

    private languageSettings = this.store.selectSignal(selectI18N)();

    loadUserLocationResources$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadUserLocations),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([action, vs]) => {
                return from(this.appService.userLocationService.getUserLocationResources(vs.Database, action.locations, action.fq)).pipe(
                    map(res => {
                        if (res.status) {
                            return UserRecordActions.setUsers({ records: res.data });
                        }
                    }),
                    catchError(_err => {
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadWastebinObjects$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadWastebinObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                const databases = getDatabasesOptions(
                    vs as StorytellingSingleViewStateRecord,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.PSTGroup,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                if (databases.length === 0) {
                    return of(ApplicationActions.noOpAction());
                }
                return forkJoin(
                    databases.map(database => {
                        return this.playbookService.loadWasteBinObjectsObservable(vs, database);
                    })
                ).pipe(
                    takeUntil(this.actions$.pipe(ofType(ViewStateActions.setCurrentViewStateSuccess))),
                    switchMap(response => {
                        let topics = new Map<string, StorytellingTopicRecord>();
                        let stories = new Map<string, StorytellingStoryRecord>();
                        let rundowns = new Map<string, StorytellingRundownRecord>();
                        let listGuids = new Set<string>();
                        let guids = new Set<string>();
                        response.forEach(res => {
                            if (res.status) {
                                topics = new Map([...topics, ...res.data.topics]);
                                stories = new Map([...stories, ...res.data.stories]);
                                rundowns = new Map([...rundowns, ...res.data.rundowns]);
                                listGuids = new Set([...listGuids, ...res.data.listGuids]);
                            }
                        });
                        guids = new Set([...guids, ...topics.keys()]);
                        const actions: Action[] = [
                            TopicRecordActions.setTopics({ records: topics }),
                            StoriesRecordActions.setStories({ records: stories }),
                            RundownRecordActions.setRundowns({ records: rundowns }),
                            ListActions.setListGuids({ listGuids: listGuids }),
                            ListActions.startPolling(),
                            TopicRecordActions.setAttachedTopicGuids({ guids: guids }),
                        ];

                        return actions;
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadTemplatesObjects$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadTemplatesObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                const databases = getDatabasesOptions(
                    vs as StorytellingSingleViewStateRecord,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.PSTGroup,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                return forkJoin(
                    databases.map(database => {
                        return this.templatesService.loadTemplatesObjectsObservable(vs, database);
                    })
                ).pipe(
                    takeUntil(this.actions$.pipe(ofType(ViewStateActions.setCurrentViewStateSuccess))),
                    switchMap(response => {
                        let rundowns = new Map<string, StorytellingRundownRecord>();
                        let listGuids = new Set<string>();
                        response.forEach(res => {
                            if (res.status) {
                                rundowns = new Map([...rundowns, ...res.data.rundowns]);
                                listGuids = new Set([...listGuids, ...res.data.listGuids]);
                            }
                        });
                        const actions: Action[] = [
                            ListActions.setListGuids({ listGuids: listGuids }),
                            ListActions.startPolling(),
                            RundownRecordActions.setRundownTemplates({ templates: [...rundowns.values()], records: rundowns }),
                        ];

                        return actions;
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadPlaybookObjects$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                const isSearch = vs.PSTViewStateType === ViewStateTypes.SEARCH;
                const databases = getDatabasesOptions(
                    vs as StorytellingSingleViewStateRecord,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.PSTGroup,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                if (databases.length === 0) {
                    return of(ApplicationActions.noOpAction());
                }
                return forkJoin(
                    databases.map(database => {
                        return this.playbookService.loadPlaybookObjectsObservable(vs, null, database);
                    })
                ).pipe(
                    takeUntil(this.actions$.pipe(ofType(ViewStateActions.setCurrentViewStateSuccess))),
                    switchMap(response => {
                        let topics = new Map<string, StorytellingTopicRecord>();
                        let assignments = new Map<string, StorytellingAssignmentRecord>();
                        let stories = new Map<string, StorytellingStoryRecord>();
                        let discoverItems = new Map<string, StorytellingRecord>();
                        let listGuids = new Set<string>();
                        let guids = new Set<string>();
                        response.forEach(res => {
                            if (res.status) {
                                topics = new Map([...topics, ...res.data.topics]);
                                assignments = new Map([...assignments, ...res.data.assignments]);
                                stories = new Map([...stories, ...res.data.stories]);
                                discoverItems = new Map([...discoverItems, ...res.data.discoverItems]);
                                listGuids = new Set([...listGuids, ...res.data.listGuids]);
                            }
                        });
                        guids = new Set([...guids, ...topics.keys()]);
                        const actions: Action[] = [
                            TopicRecordActions.setTopics({ records: topics }),
                            AssignmentRecordActions.setAssignments({ records: assignments }),
                            StoriesRecordActions.setStories({ records: stories }),
                            DiscoverRecordActions.setDiscoverItems({ records: discoverItems }),
                            ListActions.setListGuids({ listGuids }),
                            // For SEARCH, chain into loadRundowns$ before starting polling to avoid
                            // a race condition where polling fires before rundown guids are in the store.
                            // For all other view states, start polling immediately after topics are loaded.
                            isSearch ? RecordHttpActions.loadRundowns({ vs }) : ListActions.startPolling(),
                            TopicRecordActions.setAttachedTopicGuids({ guids: guids }),
                        ];

                        return actions;
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadSingleItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadSingleItem),
            concatLatestFrom(() => [this.store.select(selectSystemSingleViewStateItem), this.store.select(selectCurrentApplication)]),
            switchMap(([action, item, application]) => {
                if (application === AppName.PLAYBOOK) {
                    return this.playbookService.loadPlaybookObjectsObservable(item.ViewState, action.guid).pipe(
                        switchMap(res => {
                            if (res.status) {
                                const isTopic = !!res.data.topics.get(action.guid);
                                const isAssignment = !!res.data.assignments.get(action.guid);
                                const isStory = !!res.data.stories.get(action.guid);

                                if (isTopic) {
                                    const title = res.data.topics.get(action.guid).Title;
                                    return [
                                        NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.TOPIC, title: title }),
                                        TopicRecordActions.setTopics({ records: res.data.topics }),
                                        AssignmentRecordActions.setAssignments({ records: res.data.assignments }),
                                        StoriesRecordActions.setStories({ records: res.data.stories }),
                                        ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                    ];
                                }
                                if (isAssignment) {
                                    const assignment = res.data.assignments.get(action.guid);
                                    const title = assignment.Title;
                                    return [
                                        NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.ASSIGNMENT, title: title }),
                                        TopicRecordActions.setTopics({ records: res.data.topics }),
                                        AssignmentRecordActions.setAssignments({ records: res.data.assignments }),
                                        ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                    ];
                                }
                                if (isStory) {
                                    const title = res.data.stories.get(action.guid).Title;
                                    return [
                                        NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.SCRIPT, title: title }),
                                        TopicRecordActions.setTopics({ records: res.data.topics }),
                                        StoriesRecordActions.setStories({ records: res.data.stories }),
                                        ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                    ];
                                }
                            } else {
                                return throwError(() => {
                                    'Failed to load item';
                                });
                            }
                        }),
                        catchError(err => {
                            console.error(err);
                            return of(ApplicationActions.noOpAction());
                        })
                    );
                } else if (application === AppName.LIVE) {
                    const vs = item.ViewState as StorytellingSingleViewStateRecord;

                    // When loading a single item by GUID (URL paste), search across ALL folders, not just current folder
                    const existingFilters = vs.PSTViewStateFilter?.Filters || [];
                    const hasServerLocationsFilter = existingFilters.some(f => f.ID === PSTFieldNames.PSTServerLocations);
                    const updatedFilters = hasServerLocationsFilter
                        ? existingFilters.map(filter =>
                              filter.ID === PSTFieldNames.PSTServerLocations ? { ...filter, Value: [{ id: OPTION_ALL, title: this.languageSettings.PSTWebAllLabel }] } : filter
                          )
                        : [...existingFilters, { ID: PSTFieldNames.PSTServerLocations, Value: [{ id: OPTION_ALL, title: this.languageSettings.PSTWebAllLabel }] }];
                    const vsForAllLocations: StorytellingSingleViewStateRecord = {
                        ...vs,
                        PSTViewStateFilter: {
                            ...vs.PSTViewStateFilter,
                            Filters: updatedFilters,
                        },
                    };

                    const databases = getDatabasesOptions(
                        vsForAllLocations,
                        this.nomWebServicesJson.PSTServer,
                        this.nomWebServicesJson.HelloResults.HomeProgram,
                        this.globalTablesService.AllGroups,
                        this.globalTablesService.AllGroupsArray
                    );
                    if (databases.length === 0) {
                        return of(ApplicationActions.noOpAction());
                    }
                    return forkJoin(
                        databases.map(database => {
                            return this.liveSearchService.getSearchResultsObservable(vsForAllLocations, database, action.guid);
                        })
                    ).pipe(
                        switchMap(responses => {
                            const rundowns = new Map<string, StorytellingRundownRecord>();
                            const listGuids = new Set<string>();
                            let rundown: StorytellingRundownRecord = null;

                            responses.forEach(res => {
                                if (res.status && res.data.rundowns) {
                                    res.data.rundowns.forEach((ro, guid) => {
                                        if (guid === action.guid) {
                                            rundowns.set(guid, ro);
                                            listGuids.add(guid);
                                            rundown = ro;
                                        }
                                    });
                                }
                            });

                            if (rundown) {
                                return [
                                    NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.RUNDOWN, title: rundown.Title }),
                                    RundownRecordActions.setRundowns({ records: rundowns }),
                                    ListActions.setListGuids({ listGuids: listGuids }),
                                ];
                            } else {
                                return throwError(() => 'Failed to load item: Rundown not found in any database');
                            }
                        }),
                        catchError(err => {
                            console.error(err);
                            return of(ApplicationActions.noOpAction());
                        })
                    );
                } else {
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    loadNewItems$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadNewItems),
            concatLatestFrom(() => this.store.select(selectListNewGuids)),
            exhaustMap(([_, guids]) => {
                return this.playbookSearchService.getNewItemsObservable(guids).pipe(
                    exhaustMap(res => {
                        if (res.status) {
                            return [
                                TopicRecordActions.updateTopics({ records: [...res.data.topics.values()] }),
                                AssignmentRecordActions.updateAssignments({ records: [...res.data.assignments.values()] }),
                                StoriesRecordActions.updateStories({ records: [...res.data.stories.values()] }),
                            ];
                        }
                        return throwError(() => {
                            'Failed to load new items';
                        });
                    }),
                    catchError(err => {
                        console.error(err);
                        // not sure if we need this
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadRundowns),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([action, currentVs]) => {
                const vs = action.vs || currentVs;
                if (isSingleViewStateRecord(vs)) {
                    const isSearch = vs.PSTViewStateType === ViewStateTypes.SEARCH;
                    const databases = getDatabasesOptions(
                        vs,
                        this.nomWebServicesJson.PSTServer,
                        this.nomWebServicesJson.HelloResults.HomeProgram,
                        this.globalTablesService.AllGroups,
                        this.globalTablesService.AllGroupsArray
                    );

                    if (databases.length === 0) {
                        // Even with no databases, SEARCH must still dispatch startPolling to
                        // complete the loading chain started by loadPlaybookObjects$.
                        return isSearch ? of(ListActions.startPolling()) : of(ApplicationActions.noOpAction());
                    }

                    return forkJoin(
                        databases.map(database => {
                            return this.rundownService.loadRundownsV2Observable(vs, database);
                        })
                    ).pipe(
                        takeUntil(this.actions$.pipe(ofType(ViewStateActions.setCurrentViewStateSuccess))),
                        switchMap(response => {
                            let rundowns = new Map<string, StorytellingRundownRecord>();
                            let stories = new Map<string, StorytellingStoryRecord>();
                            let listGuids = new Set<string>();
                            response.forEach(res => {
                                if (res.status) {
                                    rundowns = new Map([...rundowns, ...res.data.rundowns]);
                                    stories = new Map([...stories, ...res.data.stories]);
                                    listGuids = new Set([...listGuids, ...res.data.listGuids]);
                                }
                            });
                            if (isSearch) {
                                // For SEARCH, merge rundown guids into the existing list (addListGuids)
                                // rather than replacing it (setListGuids), and update stories rather than replacing
                                // them, since topics/stories were already set by loadPlaybookObjects$.
                                // startPolling is dispatched here to complete the loading chain.
                                return [
                                    RundownRecordActions.setRundowns({ records: rundowns }),
                                    StoriesRecordActions.updateStories({ records: [...stories.values()] }),
                                    ListActions.addListGuids({ listGuids: Array.from(listGuids) }),
                                    ListActions.startPolling(),
                                ];
                            }
                            return [
                                RundownRecordActions.setRundowns({ records: rundowns }),
                                StoriesRecordActions.setStories({ records: stories }),
                                ListActions.setListGuids({ listGuids: listGuids }),
                                ListActions.startPolling(),
                            ];
                        }),
                        catchError(err => {
                            console.error(err);
                            return of(ApplicationActions.noOpAction());
                        })
                    );
                } else {
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    moveRecordToGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.moveRecordToGroup),
            concatLatestFrom(() => [
                this.store.select(selectCurrentViewState),
                this.store.select(selectTopicRecordsMap),
                this.store.select(selectCurrentGroupBy),
                this.store.select(selectCurrentSortBy),
                this.store.select(selectCurrentSortByOrder),
                this.store.select(selectFieldDefsDict),
                this.store.select(selectGroupedSortedTopics),
                this.store.select(selectI18N),
                this.store.select(selectAssignmentRecordsMap),
            ]),
            switchMap(([action, viewState, topicsMap, groupBy, sortBy, sortByOrder, fieldDefs, sortedTopics, i18n, assignmentsMap]) => {
                const isTimeField = groupBy.endsWith(TIME_FIELD_SUFFIX);
                const fieldDef = isTimeField ? fieldDefs[groupBy] || fieldDefs[groupBy.replace(TIME_FIELD_SUFFIX, '')] : fieldDefs[groupBy];
                const sourceGroup = action.sourceGroup;
                const targetGroup = action.targetGroup;

                let sourceRecord = null;
                let targetRecord = null;
                switch (viewState.PSTViewStateType) {
                    case ViewStateTypes.TOPIC:
                        sourceRecord = topicsMap.get(action.sourceGuid);
                        if (action.targetGuid) {
                            targetRecord = topicsMap.get(action.targetGuid);
                        }
                        break;
                    case ViewStateTypes.ASSIGNMENT:
                        sourceRecord = assignmentsMap.get(action.sourceGuid);
                        if (action.targetGuid) {
                            targetRecord = assignmentsMap.get(action.targetGuid);
                        }
                        break;
                }

                const params = {
                    sortBy: sortBy,
                    sortByOrder: sortByOrder,
                    groupBy: groupBy,
                    sourceRecord: sourceRecord,
                    sourceGroup: sourceGroup,
                    targetRecord: targetRecord,
                    targetGroup: targetGroup,
                    viewStateGuid: viewState.Guid,
                    sortedTopics: sortedTopics,
                    fieldDef: fieldDef,
                    isTileView: !!action.isTileView,
                    isTimeField,
                };

                if (['checklistedit', 'staffidlist', 'checklist'].indexOf(fieldDef.Validation.toLowerCase()) > -1) {
                    const value = sourceRecord[fieldDef.ID];

                    if (value && value.length > 1) {
                        const modalContent = i18n.PSTWebConfirmChangeTopicGroup?.replace('{fieldName}', fieldDef.DefaultCaption);
                        return this.modalService
                            .openModal({
                                title: 'Update item',
                                content: modalContent,
                                autofocus: 'NoButton',
                            })
                            .result.pipe(
                                filter(res => res === ModalResponseCodes.OK),
                                map(() => params)
                            );
                    }
                }
                return of(params);
            }),
            switchMap(params => {
                if (params.sortBy === PSTFieldNames.PSTTopicOrder) {
                    let actions: Action[] = [];

                    const targetGroupRecords = [...(params.sortedTopics.entries[params.targetGroup] || [])];
                    let sourceGroupRecords = [...(params.sortedTopics.entries[params.sourceGroup] || [])];
                    if (params.sourceGroup === params.targetGroup) {
                        sourceGroupRecords = targetGroupRecords;
                    }

                    if (!params.targetRecord && params.sortedTopics.entries[params.targetGroup]?.length > 0) {
                        params.targetRecord = params.sortedTopics.entries[params.targetGroup][0];
                    }

                    const sourceIndex = sourceGroupRecords.findIndex(rec => rec.Guid === params.sourceRecord.Guid);
                    const [record] = sourceGroupRecords.splice(sourceIndex, 1);
                    let targetIndex = targetGroupRecords.findIndex(rec => rec.Guid === params.targetRecord.Guid);

                    if (targetIndex === -1) {
                        targetIndex = 0;
                    }

                    targetGroupRecords.splice(targetIndex, 0, record);

                    let order = params.sortByOrder === 'desc' ? targetGroupRecords.length * 1000 : 1000;
                    for (const record of targetGroupRecords) {
                        const currentViewStateOrder: ViewStateOrder = record[PSTFieldNames.PSTTopicOrder] ? (record[PSTFieldNames.PSTTopicOrder] as ViewStateOrder).deepCopy() : new ViewStateOrder();
                        const currentRecordOrder: number = currentViewStateOrder.get(params.viewStateGuid);

                        if (!currentRecordOrder || currentRecordOrder === 0 || currentRecordOrder !== order) {
                            currentViewStateOrder.set(params.viewStateGuid, order);
                            const data: { key: string; value: any }[] = [];
                            data.push({
                                key: PSTFieldNames.PSTTopicOrder,
                                value: currentViewStateOrder,
                            });
                            data.push({
                                key: PSTFieldNames.Type,
                                value: record.Type,
                            });

                            getTopicFieldsToUpdate(params.sourceRecord, params.fieldDef, params.targetGroup, params.isTimeField).forEach(field => {
                                data.push({
                                    key: field.key,
                                    value: field.value,
                                });
                            });

                            actions = [...actions, RecordHttpActions.saveRecordChanges({ record: record, fieldsToUpdate: data })];
                        }
                        if (params.sortByOrder === 'desc') {
                            order -= 1000;
                        } else {
                            order += 1000;
                        }
                    }
                    actions = [TopicRecordActions.updateTopics({ records: targetGroupRecords }), ...actions];
                    return actions;
                } else {
                    const fieldsToUpdate = getTopicFieldsToUpdate(params.sourceRecord, params.fieldDef, params.targetGroup, params.isTimeField);
                    return of(RecordHttpActions.saveRecordChanges({ record: params.sourceRecord, fieldsToUpdate: fieldsToUpdate }));
                }
            })
        );
    });

    // TODO: Might need a better name
    saveChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.saveRecordChanges),
            concatLatestFrom(() => [this.store.select(selectFullscreenNewRecord)]),
            concatMap(([action, newRecord]) => {
                const record = { ...action.record };
                if (!record || record?.Path?.isWasteBinPath) {
                    return of(ApplicationActions.noOpAction());
                }
                const fieldsToUpdate = action.fieldsToUpdate;
                const data: Record<string, any> = {};
                if (record?.isNewRecord) {
                    for (const field of fieldsToUpdate) {
                        data[field.key] = field.value;
                        record[field.key] = field.value;
                    }

                    switch (newRecord.Type) {
                        case ObjectTypes.SCRIPT: {
                            return of(FullscreenActions.updateCurrentNewRecord({ record: record as StorytellingStoryRecord }));
                        }
                        case ObjectTypes.TOPIC: {
                            // TODO: check this
                            if (record.Type === ObjectTypes.SCRIPT) {
                                //Update existing record
                                const storyIdx = (newRecord as StorytellingTopicRecord)?.stories?.findIndex(story => story.Guid === record.Guid);
                                if (storyIdx != null && storyIdx !== -1) {
                                    (record as StorytellingStoryRecord).FullStorySlug = record.Title;
                                    const newStories = [...(newRecord as StorytellingTopicRecord).stories];
                                    newStories[storyIdx] = { ...record };
                                    const updatedRecord = { ...newRecord, stories: newStories };
                                    return of(FullscreenActions.updateCurrentNewRecord({ record: updatedRecord }));
                                }
                            } else if (record.Type === ObjectTypes.ASSIGNMENT) {
                                //Update existing record
                                const assignmentIdx = (newRecord as StorytellingTopicRecord)?.children?.findIndex(assignment => assignment.Guid === record.Guid);
                                if (assignmentIdx != null && assignmentIdx !== -1) {
                                    const newAssignments = [...(newRecord as StorytellingTopicRecord).children];
                                    newAssignments[assignmentIdx] = record;
                                    const updatedRecord = { ...newRecord, children: newAssignments };
                                    return of(FullscreenActions.updateCurrentNewRecord({ record: updatedRecord }));
                                }
                            }
                            return of(FullscreenActions.updateCurrentNewRecord({ record, fieldsToUpdate: data }));
                        }
                    }
                } else {
                    //Update existing record
                    const titleField = fieldsToUpdate.find(field => field.key === PSTFieldNames.Title);
                    if (titleField && typeof titleField.value === 'string' && titleField.value.toString().trim() === '') {
                        return of(ApplicationActions.noOpAction());
                    }

                    if (!record?.Guid || record.Guid === BLACK_LINE_GUID) {
                        return of(ApplicationActions.noOpAction());
                    }

                    let updatedTitle = '';
                    for (const field of fieldsToUpdate) {
                        // to prevent moving the story to a different topic
                        if (field.key === PSTFieldNames.PSTStoryTopicGUID && record.Type === ObjectTypes.SCRIPT && (record as StorytellingStoryRecord).PSTStoryTopicGUID) {
                            continue;
                        }

                        if (field.key === PSTFieldNames.TextCommands) {
                            continue;
                        }

                        // when updating script, we need to update the text commands, but not for assignments
                        if (record.Type === ObjectTypes.SCRIPT) {
                            if (field.key === PSTFieldNames.PSTRichText || field.key === PSTFieldNames.PSTDraftText) {
                                const textCommands = getTextCommandsFromText(field.value);
                                data['TextCommandKeys'] = textCommands.TextCommandKeys;
                                data['TextCommandValues'] = textCommands.TextCommandValues;
                            } else if (field.key === PSTFieldNames.Title) {
                                updatedTitle = field.value;
                            }
                        }

                        let enpsField: EnpsField;
                        // when updating assignment, we don't need to serialize the field
                        if (record.Type === ObjectTypes.ASSIGNMENT && (field.key === PSTFieldNames.PSTRichText || field.key === PSTFieldNames.PSTDraftText)) {
                            enpsField = field;
                        } else {
                            enpsField = this.convertor.serializeField(field.value, field.key, record.Type);
                        }

                        if (!(typeof field.value === 'string' || typeof field.value === 'number' || typeof field.value === 'boolean') && enpsField.usingRawValue) {
                            Util.saveWarningError(`EnpsControl.saveChanges() not implemented for type ${typeof field.value}`);
                            return;
                        }

                        data[enpsField.key] = enpsField.value;
                    }

                    // Make sure to pass record type
                    data.Type = record.Type;

                    const database = record.Database || this.nomWebServicesJson.PSTServer;

                    const updateRecords = (loadedRo?: StorytellingRundownRecord) => {
                        return from(this.basicContentService.updateBasicContentV2(database, record.Path.fullPath, record.Guid, false, data, false, record)).pipe(
                            concatMap(res => {
                                if (!res.status) {
                                    throw new Error('Error updating record');
                                }
                                const actions = [];
                                if (loadedRo) {
                                    actions.push(RundownRecordActions.updateRundowns({ records: [loadedRo] }));
                                }

                                // if we are saving from properties dialog, close it
                                const propertiesDialog = this.dialog.getDialogById(RUNDOWN_PROPERTIES_DIALOG_ID);
                                if (propertiesDialog) {
                                    propertiesDialog.close();
                                }

                                // TODO: ALL TYPES OF RECORDS
                                switch (record.Type) {
                                    case ObjectTypes.TOPIC:
                                        return [TopicRecordActions.updateTopics({ records: [res.data] }), FullscreenActions.setLoadingProgressBar({ showLoader: false }), ...actions];
                                    case ObjectTypes.ASSIGNMENT:
                                        return [AssignmentRecordActions.updateAssignments({ records: [res.data] }), FullscreenActions.setLoadingProgressBar({ showLoader: false }), ...actions];
                                    case ObjectTypes.SCRIPT: {
                                        return [
                                            StoriesRecordActions.setFlashState({ record: res.data }),
                                            StoriesRecordActions.updateStories({ records: [res.data] }),
                                            FullscreenActions.setLoadingProgressBar({ showLoader: false }),
                                            ...actions,
                                        ];
                                    }
                                    case ObjectTypes.RUNDOWN:
                                        return [
                                            record.Path?.isTemplate()
                                                ? RundownRecordActions.updateRundownTemplates({ templates: [res.data] })
                                                : RundownRecordActions.updateRundowns({ records: [res.data] }),
                                            FullscreenActions.setLoadingProgressBar({ showLoader: false }),
                                            PlaybookApiActions.createRecordDone(),
                                        ];
                                    case ObjectTypes.NOTE:
                                        return [
                                            NotesRecordActions.updateNotes({ records: [res.data] }),
                                            FullscreenActions.setLoadingProgressBar({ showLoader: false }),
                                            PlaybookApiActions.createRecordDone(),
                                        ];
                                    default:
                                        return [FullscreenActions.setLoadingProgressBar({ showLoader: false }), ApplicationActions.noOpAction()];
                                }
                            })
                        );
                    };

                    // Check if the record is story and is in Rundown
                    const storyInRo = record?.Type === ObjectTypes.SCRIPT && isStoryInRundown(record);

                    // Warn if title change affects sub-story grouping
                    if (updatedTitle && record.Type === ObjectTypes.SCRIPT && updatedTitle !== record.Title) {
                        const storyRecord = record as StorytellingStoryRecord;
                        const titleSeparator = this.store.selectSignal(selectTitleSeparator)();
                        const subStoriesMap = this.store.selectSignal(selectSubStoriesByParentGuid)();
                        const hasSubStories = (subStoriesMap.get(record.Guid)?.length || 0) > 0;
                        const isSubStory = !!storyRecord.PSTPrimaryStoryGUID;

                        // Check if the substory prefix (part before separator) changed
                        let subStoryPrefixChanged = false;
                        if (isSubStory) {
                            const sepPattern = `${titleSeparator} `;
                            const oldLastSepIdx = storyRecord.Title?.lastIndexOf(sepPattern);
                            const newLastSepIdx = updatedTitle.lastIndexOf(sepPattern);
                            const oldPrefix = storyRecord.Title.substring(0, oldLastSepIdx);
                            const newPrefix = newLastSepIdx > -1 ? updatedTitle.substring(0, newLastSepIdx) : updatedTitle;
                            subStoryPrefixChanged = oldPrefix !== newPrefix;
                        }

                        if (hasSubStories || subStoryPrefixChanged) {
                            const i18n = this.store.selectSignal(selectI18N)();
                            return this.modalService
                                .openModal({
                                    title: i18n[I18nKeys.PSTWebModalWarningTitle],
                                    content: i18n[I18nKeys.PSTWebSubStoryTitleChangeWarning],
                                    autofocus: 'NoButton',
                                    disableClose: true,
                                })
                                .result.pipe(
                                    take(1),
                                    concatMap(modalResponse => {
                                        if (modalResponse !== ModalResponseCodes.OK) {
                                            // User chose NO — revert the story state
                                            const storyToRevert = this.store.selectSignal(selectStoryRecordsMap)().get(record.Guid);
                                            return of(
                                                StoriesRecordActions.updateStories({
                                                    records: [{ ...storyToRevert, internalModTime: DateTime.now() }],
                                                }),
                                                FullscreenActions.setLoadingProgressBar({ showLoader: false })
                                            );
                                        }
                                        // User chose YES — proceed with the title change for this record only
                                        // If in rundown, still perform duplicate slug check before saving
                                        if (storyInRo) {
                                            let newlyLoadedRO: StorytellingRundownRecord | null = null;
                                            const ro = this.store.selectSignal(selectRundownsWithStories)()?.get(record.Path.parentCollectionGuid);
                                            const roObs = ro
                                                ? of(ro)
                                                : from(this.rundownService.getRundown(record.Database, record.Path.parentCollectionPath.fullPath, record.Path.parentCollectionGuid)).pipe(
                                                      filter(response => response.status),
                                                      map(response => response.data),
                                                      map(loadedRo => {
                                                          newlyLoadedRO = loadedRo;
                                                          return loadedRo;
                                                      })
                                                  );
                                            return roObs.pipe(
                                                concatMap(r => {
                                                    return checkForDuplicateSlugObs(r, { ...record, Title: updatedTitle }, titleSeparator);
                                                }),
                                                concatMap(duplicate => {
                                                    if (duplicate) {
                                                        const lang = this.store.selectSignal(selectI18N)();
                                                        this.modalService.openAlert({
                                                            title: lang[I18nKeys.PSTWebModalWarningTitle],
                                                            content: lang[I18nKeys.PSTWebDuplicateSlugAlertContent].replace('{slug}', duplicate.Title).replace('{rundown}', ro?.Title || ''),
                                                        });
                                                        const storyToRevert = this.store.selectSignal(selectStoryRecordsMap)().get(record.Guid);
                                                        return of(
                                                            StoriesRecordActions.updateStories({
                                                                records: [{ ...storyToRevert, internalModTime: DateTime.now() }],
                                                            }),
                                                            FullscreenActions.setLoadingProgressBar({ showLoader: false })
                                                        );
                                                    }
                                                    return updateRecords(newlyLoadedRO);
                                                })
                                            );
                                        }
                                        return updateRecords();
                                    })
                                );
                        }
                    }

                    // If the record is story and is in Rundown, check for duplicate slug if the title is updated
                    if (storyInRo && updatedTitle) {
                        // Get the Rundown, if not available, load it
                        let newlyLoadedRO: StorytellingRundownRecord | null = null;
                        const getStoryRundownObs = (story: StorytellingStoryRecord) => {
                            const ro = this.store.selectSignal(selectRundownsWithStories)()?.get(story.Path.parentCollectionGuid);
                            if (!ro) {
                                return from(this.rundownService.getRundown(story.Database, story.Path.parentCollectionPath.fullPath, story.Path.parentCollectionGuid)).pipe(
                                    filter(response => response.status),
                                    map(response => response.data),
                                    map(loadedRo => {
                                        // Store the loaded Rundown, so later we can dispatch updateRundowns
                                        newlyLoadedRO = loadedRo;
                                        return loadedRo;
                                    })
                                );
                            }
                            return of(ro);
                        };

                        let destRundown = null;
                        return getStoryRundownObs(record).pipe(
                            concatMap(r => {
                                const titleSeparator = this.store.selectSignal(selectTitleSeparator)();
                                destRundown = r;
                                // Check for duplicate slug
                                return checkForDuplicateSlugObs(
                                    r,
                                    {
                                        ...record,
                                        Title: updatedTitle,
                                    },
                                    titleSeparator
                                );
                            }),
                            concatMap(duplicate => {
                                // If duplicate slug is found, show alert and return false
                                if (duplicate) {
                                    const languageSettings = this.store.selectSignal(selectI18N)();
                                    this.modalService.openAlert({
                                        title: languageSettings[I18nKeys.PSTWebModalWarningTitle],
                                        content: languageSettings[I18nKeys.PSTWebDuplicateSlugAlertContent].replace('{slug}', duplicate.Title).replace('{rundown}', destRundown?.Title),
                                    });

                                    return of(false);
                                }
                                return of(true);
                            }),
                            concatMap(shouldUpdate => {
                                // If check dublicate has returned skip update, otherwise update the records
                                if (shouldUpdate) {
                                    return updateRecords(newlyLoadedRO);
                                } else {
                                    // we need to revert the story state
                                    const storyToRevert = this.store.selectSignal(selectStoryRecordsMap)().get(record.Guid);
                                    const actions: Action[] = [
                                        FullscreenActions.setLoadingProgressBar({ showLoader: false }),
                                        StoriesRecordActions.updateStories({
                                            records: [
                                                {
                                                    ...storyToRevert,
                                                    internalModTime: DateTime.now(),
                                                },
                                            ],
                                        }),
                                    ];
                                    // If we have a newly loaded Rundown, update it in the store
                                    if (newlyLoadedRO) {
                                        actions.push(RundownRecordActions.updateRundowns({ records: [newlyLoadedRO] }));
                                    }
                                    return actions;
                                }
                            })
                        );
                    }
                    return updateRecords();
                }
            }),
            catchError(err => {
                console.error(err);
                return of(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
            })
        );
    });

    subscribeForRealtimeUpdates$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    TopicRecordActions.setTopics,
                    AssignmentRecordActions.setAssignments,
                    StoriesRecordActions.setStories,
                    RundownRecordActions.setRundowns,
                    RundownRecordActions.setRundownTemplates,
                    PublicationRecordActions.setPublicationChannels,
                    PublicationRecordActions.setPublications,
                    PublicationRecordActions.setPublicationList
                ),
                mergeMap(action => {
                    return forkJoin([
                        Array.from(action.records ?? []).map(rec => {
                            // TODO this needs to be an effect and we need to store subscribed records in the store
                            from(DataStore.subscribeForRecordUpdatesV2(rec[1]));
                            return rec;
                        }),
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    subscribeForRealtimeUpdatesUpdate$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    TopicRecordActions.updateTopics,
                    AssignmentRecordActions.updateAssignments,
                    StoriesRecordActions.updateStories,
                    RundownRecordActions.updateRundowns,
                    PublicationRecordActions.updatePublications,
                    PublicationRecordActions.updatePublicationList,
                    PublicationRecordActions.updatePublicationChannels
                ),
                mergeMap(action => {
                    return forkJoin([
                        action.records.map((rec: StorytellingRecord) => {
                            // TODO this needs to be an effect and we need to store subscribed records in the store
                            from(DataStore.subscribeForRecordUpdatesV2(rec));
                            return rec;
                        }),
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    resetRowFlashingState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.setFlashState),
            delay(2000),
            map(action => {
                return StoriesRecordActions.resetFlashState({ record: action.record });
            })
        );
    });

    duplicateTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.duplicateTopic),
            concatLatestFrom(() => [this.store.select(selectCurrentViewStateType), this.store.select(selectCurrentRecord)]),
            switchMap(([action, currentViewStateType, currentRecord]) => {
                return from(this.playbookService.duplicateItem(action.linkToParent, action.oldRecord, action.isNew)).pipe(
                    map(rec => rec.data as StorytellingTopicRecord),
                    switchMap(newRecord => {
                        let actions: Action[] = [
                            TopicRecordActions.updateTopics({ records: [newRecord] }),
                            AssignmentRecordActions.updateAssignments({ records: newRecord.children || [] }),
                            StoriesRecordActions.updateStories({ records: newRecord.stories || [] }),
                        ];
                        if (currentRecord) {
                            actions = [...actions, FullscreenActions.openTopic({ recordGuid: newRecord?.Guid, recordType: newRecord?.Type })];
                        } else {
                            actions = [...actions, OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: newRecord?.Guid, recordType: newRecord?.Type })];
                        }
                        if (currentViewStateType === ViewStateTypes.TOPIC) {
                            actions = [...actions, ListActions.addListGuids({ listGuids: [newRecord.Guid] })];
                        }
                        return actions;
                    })
                );
            })
        );
    });

    duplicateAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.duplicateAssignment),
            concatLatestFrom(action => {
                return [this.store.select(selectAssignments), this.store.select(selectTopicRecordByGuid(action.record.ParentGuid)), this.store.select(selectCurrentViewStateType)];
            }),
            switchMap(([action, assignments, parentRecord, currentViewStateType]) => {
                return from(this.playbookService.duplicateAssignment(action.record, action.linkToParent)).pipe(
                    map(rec => rec.data),
                    switchMap(newRecord => {
                        assignments.push(newRecord);
                        let actions: Action[] = [TopicRecordActions.updateTopics({ records: [parentRecord] }), AssignmentRecordActions.updateAssignments({ records: assignments || [] })];
                        if (currentViewStateType === ViewStateTypes.ASSIGNMENT) {
                            actions = [...actions, ListActions.addListGuids({ listGuids: [newRecord.Guid] })];
                        }
                        return actions;
                    })
                );
            })
        );
    });

    deleteStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.deleteStoriesFromRTU),
            concatLatestFrom(() => {
                return [this.store.select(selectFullscreenRecordGuid), this.store.select(selectTopicRecordsMap)];
            }),
            switchMap(([action, guid, topics]) => {
                const matchingRecord = action.records?.find(record => record.Guid === guid);
                if (!matchingRecord) {
                    return of(ApplicationActions.noOpAction());
                }

                const storyRec = matchingRecord as StorytellingStoryRecord;
                if (storyRec?.PSTStoryTopicGUID) {
                    const currentTopic: StorytellingTopicRecord | undefined = topics.get(storyRec.PSTStoryTopicGUID);
                    if (currentTopic?.Guid) {
                        return of(FullscreenActions.openTopic({ recordGuid: currentTopic?.Guid, recordType: ObjectTypes.TOPIC }));
                    }
                }
                return of(OpenFullscreenModalActions.closeFullscreenModal());
            })
        );
    });

    deleteRecord$ = createEffect(() => {
        const canDeleteRecord = (_: StorytellingRecord) => {
            //TODO: Left for future use when we know what we want
            /*
            if (record?.Type === ObjectTypes.SCRIPT && isStoryInRundown(record)) {
                const rundowns = this.store.selectSignal(selectRundownRecordsMap)();
                if (rundowns.get(record.Path.parentCollectionGuid)) {
                    return of(!rundowns.get(record.Path.parentCollectionGuid)?.OnAir);
                }
                return this.basicContentService.getBasicContent({ database: record.Database, path: record.Path.parentCollectionPath.fullPath, guid: record.Path.parentCollectionGuid }).pipe(
                    map(res => {
                        return !(res?.data as StorytellingRundownRecord).OnAir;
                    }),
                    catchError(() => {
                        return of(false);
                    })
                );
            }
                */
            return of(true);
        };
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteRecord),
            // TODO: Ned wants to try other approach, some day
            switchMap(action => canDeleteRecord(action.record).pipe(map(canDelete => [action, canDelete] as const))),
            switchMap(([action, canDeleteStoryRecord]) => {
                const { record } = action;
                const newRecord = this.store.selectSignal(selectFullscreenNewRecord)();
                if (newRecord && newRecord?.isNewRecord) {
                    return of(FullscreenActions.removeNewRecordItems({ record: record }));
                } else {
                    const topics = this.store.selectSignal(selectTopicRecordsMap)();
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    let updateAction: (props: { records: StorytellingRecord[] }) => Action = null;
                    switch (record?.Type) {
                        case ObjectTypes.TOPIC:
                            updateAction = TopicRecordActions.updateTopics;
                            break;
                        case ObjectTypes.ASSIGNMENT:
                            updateAction = AssignmentRecordActions.updateAssignments;
                            break;
                        case ObjectTypes.SCRIPT:
                            updateAction = StoriesRecordActions.updateStories;
                            break;
                        case ObjectTypes.RUNDOWN:
                            updateAction = RundownRecordActions.updateRundowns;
                            break;
                        case ObjectTypes.NOTE:
                            updateAction = NotesRecordActions.updateNotes;
                            break;
                    }
                    const showDeletionModal = (): Observable<{
                        responseCode?: ModalResponseCodes;
                        // in the future we can improve this into something more generic like 'data'
                        topicDeletionMode?: TopicDeletionMode;
                    }> => {
                        if (record.Type === ObjectTypes.TOPIC && topics.get(record.Guid)?.stories?.length > 0) {
                            return this.dialog
                                .open(DeleteTopicStoriesComponent, {
                                    width: '23.25rem',
                                    height: '19rem',
                                    hasBackdrop: true,
                                })
                                .afterClosed()
                                .pipe(
                                    map((topicDeletionMode: TopicDeletionMode) => {
                                        if (topicDeletionMode) {
                                            return {
                                                responseCode: ModalResponseCodes.OK,
                                                topicDeletionMode,
                                            };
                                        }
                                        return {
                                            responseCode: ModalResponseCodes.CANCEL,
                                        };
                                    })
                                );
                        } else if (record.Type === ObjectTypes.SCRIPT && !canDeleteStoryRecord) {
                            return this.modalService
                                .openAlert({
                                    title: languageSettings.PSTWebUnableToDeleteStorySlug,
                                    content: languageSettings.PSTWebUnableToDeleteStoryDescription,
                                    okString: languageSettings.PSTWebDialogAboutCloseLabel,
                                })
                                .result.pipe(
                                    map(() => {
                                        return {
                                            responseCode: ModalResponseCodes.CANCEL,
                                        };
                                    })
                                );
                        } else {
                            let modalContent = '';
                            switch (record?.Type) {
                                case ObjectTypes.TOPIC:
                                    modalContent = `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteTopicPart2}${languageSettings.PSTQuestionMark}`;
                                    break;
                                case ObjectTypes.ASSIGNMENT:
                                    modalContent = `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteAssignmentPart2}${languageSettings.PSTQuestionMark}`;
                                    break;
                                case ObjectTypes.SCRIPT:
                                    modalContent = `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteStoryPart2}${languageSettings.PSTWebConfirmDeleteStoryPart3}${languageSettings.PSTQuestionMark}`;
                                    break;
                                case ObjectTypes.RUNDOWN:
                                    modalContent = `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteRundownPart2}${languageSettings.PSTQuestionMark}`;
                                    break;
                                case ObjectTypes.NOTE:
                                    modalContent = `${languageSettings.PSTWebConfirmDeleteNotePart1} ${(record as StorytellingNoteRecord).PSTNoteStatus?.title} ${languageSettings.PSTWebNoteLabel}${languageSettings.PSTQuestionMark}`;
                                    break;
                            }
                            return this.modalService
                                .openModal({
                                    title: languageSettings.PSTWebConfirmDeleteLabel,
                                    content: modalContent,
                                })
                                .result.pipe(
                                    map(modalRes => {
                                        return {
                                            responseCode: modalRes as ModalResponseCodes,
                                        };
                                    })
                                );
                        }
                    };
                    return showDeletionModal().pipe(
                        switchMap(modalRes => {
                            if (modalRes.responseCode !== ModalResponseCodes.OK) {
                                return of(updateAction({ records: [{ ...record, isDeleted: false }] }));
                            }
                            return this.basicContentService
                                .deleteBasicContent({
                                    database: record?.Database,
                                    path: record?.Path.fullPath,
                                    guid: record?.Guid,
                                    type: record?.Type,
                                    record,
                                    deleteChildRecords: modalRes.topicDeletionMode === TopicDeletionMode.DELETE_ALL,
                                })
                                .pipe(
                                    switchMap(res => {
                                        if (!res.status) {
                                            if (res.message === 'Object Locked') {
                                                return of(ApplicationActions.noOpAction());
                                            }
                                            throw new Error('Error deleting record');
                                        }

                                        const deletedRecord = res.data?.deletedRecord;
                                        const updatedRecords = res.data?.updatedRecords;
                                        const isDeletedRecordOpenInMainCard = this.store.selectSignal(selectFullscreenRecordGuid)() === deletedRecord?.Guid;
                                        const actions: Action[] = [];
                                        actions.push(updateAction({ records: [{ ...record, isDeleted: true }] }));
                                        if (isDeletedRecordOpenInMainCard && deletedRecord?.Type !== ObjectTypes.SCRIPT) {
                                            actions.push(OpenFullscreenModalActions.closeFullscreenModal());
                                        }
                                        if (deletedRecord?.Type === ObjectTypes.TOPIC) {
                                            actions.push(TopicRecordActions.deleteTopics({ records: [deletedRecord] }));
                                            actions.push(ListActions.removeListGuids({ listGuids: [deletedRecord.Guid] }));
                                            return actions;
                                        }
                                        if (deletedRecord?.Type === ObjectTypes.ASSIGNMENT) {
                                            actions.push(AssignmentRecordActions.deleteAssignments({ records: [deletedRecord] }));
                                            return actions;
                                        }
                                        if (deletedRecord?.Type === ObjectTypes.RUNDOWN) {
                                            actions.push(RundownRecordActions.updateRundowns({ records: [{ ...deletedRecord, isDeleted: true }] }));
                                            return actions;
                                        }
                                        if (deletedRecord?.Type === ObjectTypes.NOTE) {
                                            actions.push(NotesRecordActions.deleteNotes({ records: [deletedRecord] }));
                                            return actions;
                                        }
                                        if (deletedRecord?.Type === ObjectTypes.SCRIPT) {
                                            actions.push(StoriesRecordActions.deleteStories({ records: [deletedRecord] }));
                                            if (updatedRecords?.topics?.size > 0) {
                                                actions.push(TopicRecordActions.updateTopics({ records: [...updatedRecords.topics.values()] }));
                                            }
                                            if (updatedRecords?.assignments?.size > 0) {
                                                actions.push(AssignmentRecordActions.updateAssignments({ records: [...updatedRecords.assignments.values()] }));
                                            }
                                            if (updatedRecords?.stories?.size > 0) {
                                                actions.push(StoriesRecordActions.updateStories({ records: [...updatedRecords.stories.values()] }));
                                            }
                                            if (updatedRecords?.rundowns?.size > 0) {
                                                actions.push(RundownRecordActions.updateRundowns({ records: [...updatedRecords.rundowns.values()] }));
                                            }
                                            if (isDeletedRecordOpenInMainCard) {
                                                const currentTopic = topics.get((deletedRecord as StorytellingStoryRecord).PSTStoryTopicGUID);
                                                const isStandalone = this.store.selectSignal(selectIsCurrentStoryStandalone)();
                                                if (currentTopic?.Guid && !isStandalone) {
                                                    actions.push(FullscreenActions.openTopic({ recordGuid: currentTopic?.Guid, recordType: ObjectTypes.TOPIC }));
                                                } else {
                                                    actions.push(OpenFullscreenModalActions.closeFullscreenModal());
                                                }
                                            }
                                            return actions;
                                        }
                                        return [ListActions.removeListGuids({ listGuids: [deletedRecord?.Guid] })];
                                    })
                                );
                        })
                    );
                }
            })
        );
    });

    // TODO: To make back-end work with array of records that has to be deleted
    deleteRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteRecords),
            concatLatestFrom(() => {
                return [this.store.select(selectI18N)];
            }),
            switchMap(([action, languageSettings]) => {
                const { records } = action;

                if (!records?.length) {
                    return of(ApplicationActions.noOpAction());
                }

                // Group records by type
                const recordsByType = records.reduce((acc, record) => {
                    if (!acc[record.Type]) {
                        acc[record.Type] = [];
                    }
                    acc[record.Type].push(record);
                    return acc;
                }, {});

                // Create confirmation message
                let counter = 0;
                let modalContent = `${languageSettings.PSTWebConfirmDeletePart1}`;
                Object.entries(recordsByType).forEach(([type, typeRecords]) => {
                    if (!typeRecords.length) return;

                    const recordNames = typeRecords.map(r => r.Title).join(', ');
                    let singleOrPluralText = '';
                    let scriptText = '';
                    counter++;
                    const currentSign = counter < Object.entries(recordsByType).length ? '; ' : languageSettings.PSTQuestionMark;

                    switch (Number(type)) {
                        case ObjectTypes.SCRIPT:
                            scriptText = typeRecords.length > 1 ? languageSettings.PSTWebConfirmDeleteStoriesPart3 : languageSettings.PSTWebConfirmDeleteStoryPart3;
                            singleOrPluralText =
                                typeRecords.length === 1 ? languageSettings.PSTWebConfirmDeleteStoryPart2 + currentSign : languageSettings.PSTWebConfirmDeleteStoriesPart2 + currentSign;
                            break;
                        case ObjectTypes.TOPIC:
                            singleOrPluralText = typeRecords.length === 1 ? languageSettings.PSTWebConfirmDeleteTopicPart2 : languageSettings.PSTWebConfirmDeleteTopicsPart2;
                            singleOrPluralText += currentSign;
                            break;
                        case ObjectTypes.ASSIGNMENT:
                            singleOrPluralText = typeRecords.length === 1 ? languageSettings.PSTWebConfirmDeleteAssignmentPart2 : languageSettings.PSTWebConfirmDeleteAssignmentsPart2;
                            singleOrPluralText += currentSign;
                            break;
                    }

                    modalContent += `${recordNames}${singleOrPluralText}` + (Object.entries(recordsByType).length === counter ? scriptText : '');
                });
                return this.modalService
                    .openModal({
                        title: languageSettings.PSTWebConfirmDeleteLabel,
                        content: modalContent,
                    })
                    .result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        switchMap(() => {
                            // Create delete operations for all records
                            const deleteOperations = records.map(record =>
                                this.basicContentService.deleteBasicContent({ database: record?.Database, path: record?.Path?.fullPath, guid: record?.Guid, type: record?.Type, record }).pipe(
                                    map(res => ({
                                        recordGuid: record?.Guid,
                                        recordTitle: record?.Title,
                                        success: res.status,
                                        deletedRecord: res.status ? res.data?.deletedRecord : null,
                                        updatedRecords: res.status ? res.data?.updatedRecords : null,
                                        error: !res.status ? res.message : null,
                                    }))
                                )
                            );

                            return forkJoin(deleteOperations).pipe(
                                switchMap(results => {
                                    const actions: Action[] = [];
                                    const deletedRecords = [];
                                    const updates = {
                                        topics: new Map(),
                                        assignments: new Map(),
                                        stories: new Map(),
                                        rundowns: new Map(),
                                    };

                                    // Filter results with errors that are not "Object Locked"
                                    const failedResults = results.filter(result => !result.success && result.error !== 'Object Locked');

                                    // Process all successful deletions
                                    results.forEach(result => {
                                        if (result.success && result.deletedRecord) {
                                            deletedRecords.push(result.deletedRecord);

                                            // Collect all updates
                                            if (result.updatedRecords) {
                                                if (result.updatedRecords.topics?.size > 0) {
                                                    for (const [guid, topic] of result.updatedRecords.topics.entries()) {
                                                        updates.topics.set(guid, topic);
                                                    }
                                                }
                                                if (result.updatedRecords.assignments?.size > 0) {
                                                    for (const [guid, assignment] of result.updatedRecords.assignments.entries()) {
                                                        updates.assignments.set(guid, assignment);
                                                    }
                                                }
                                                if (result.updatedRecords.stories?.size > 0) {
                                                    for (const [guid, story] of result.updatedRecords.stories.entries()) {
                                                        updates.stories.set(guid, story);
                                                    }
                                                }
                                                if (result.updatedRecords.rundowns?.size > 0) {
                                                    for (const [guid, rundown] of result.updatedRecords.rundowns.entries()) {
                                                        updates.rundowns.set(guid, rundown);
                                                    }
                                                }
                                            }
                                        }
                                    });

                                    // Group deleted records by type
                                    const deletedRecordsByType = deletedRecords.reduce((acc, record) => {
                                        if (!acc[record.Type]) {
                                            acc[record.Type] = [];
                                        }
                                        acc[record.Type].push(record);
                                        return acc;
                                    }, {});

                                    // Handle Topic deletions
                                    if (deletedRecordsByType[ObjectTypes.TOPIC]?.length) {
                                        const topicRecords = deletedRecordsByType[ObjectTypes.TOPIC];

                                        actions.push(
                                            TopicRecordActions.deleteTopics({
                                                records: topicRecords,
                                            })
                                        );

                                        const topicGuids = topicRecords.map(record => record.Guid);

                                        // Only close the fullscreen when it shows one of the deleted
                                        // topics (the same guard deleteRecord$ uses).
                                        const fullscreenTopicGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                                        if (fullscreenTopicGuid && topicGuids.includes(fullscreenTopicGuid)) {
                                            actions.push(OpenFullscreenModalActions.closeFullscreenModal());
                                        }

                                        if (topicGuids.length) {
                                            actions.push(ListActions.removeListGuids({ listGuids: topicGuids }));
                                        }
                                    }

                                    // Handle Assignment deletions
                                    if (deletedRecordsByType[ObjectTypes.ASSIGNMENT]?.length) {
                                        actions.push(
                                            AssignmentRecordActions.deleteAssignments({
                                                records: deletedRecordsByType[ObjectTypes.ASSIGNMENT],
                                            })
                                        );

                                        // Get GUIDs for list removal
                                        const assignmentGuids = deletedRecordsByType[ObjectTypes.ASSIGNMENT].map(record => record.Guid);
                                        if (assignmentGuids.length) {
                                            actions.push(ListActions.removeListGuids({ listGuids: assignmentGuids }));
                                        }
                                    }

                                    // Handle Story deletions
                                    if (deletedRecordsByType[ObjectTypes.SCRIPT]?.length) {
                                        const deletedScriptGuids = new Set(deletedRecordsByType[ObjectTypes.SCRIPT].map(r => r.Guid));

                                        actions.push(
                                            StoriesRecordActions.deleteStories({
                                                records: deletedRecordsByType[ObjectTypes.SCRIPT],
                                            })
                                        );

                                        // Remove deleted stories from updates to prevent re-adding them
                                        for (const guid of deletedScriptGuids) {
                                            updates.stories.delete(guid);
                                        }

                                        // Navigate to the parent topic if the deleted story belonged to one
                                        // (matches the behavior of the single deleteRecord$ effect)
                                        const topics = this.store.selectSignal(selectTopicRecordsMap)();
                                        const isStandalone = this.store.selectSignal(selectIsCurrentStoryStandalone)();
                                        const deletedStoryWithTopic = deletedRecordsByType[ObjectTypes.SCRIPT].find(r => r.PSTStoryTopicGUID && topics.has(r.PSTStoryTopicGUID));
                                        if (deletedStoryWithTopic && !isStandalone) {
                                            actions.push(FullscreenActions.openTopic({ recordGuid: deletedStoryWithTopic.PSTStoryTopicGUID, recordType: ObjectTypes.TOPIC }));
                                        } else {
                                            const fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                                            if (fullscreenRecordGuid && deletedScriptGuids.has(fullscreenRecordGuid)) {
                                                actions.push(OpenFullscreenModalActions.closeFullscreenModal());
                                            }
                                        }
                                    }

                                    // Handle Rundown deletions
                                    if (deletedRecordsByType[ObjectTypes.RUNDOWN]?.length) {
                                        const rundownGuids = deletedRecordsByType[ObjectTypes.RUNDOWN].map(record => record.Guid);
                                        if (rundownGuids.length) {
                                            actions.push(ListActions.removeListGuids({ listGuids: rundownGuids }));
                                        }
                                    }

                                    // Always add updates regardless of record type
                                    if (updates.topics.size > 0) {
                                        actions.push(
                                            TopicRecordActions.updateTopics({
                                                records: [...updates.topics.values()],
                                            })
                                        );
                                    }

                                    if (updates.assignments.size > 0) {
                                        actions.push(
                                            AssignmentRecordActions.updateAssignments({
                                                records: [...updates.assignments.values()],
                                            })
                                        );
                                    }

                                    if (updates.stories.size > 0) {
                                        actions.push(
                                            StoriesRecordActions.updateStories({
                                                records: [...updates.stories.values()],
                                            })
                                        );
                                    }

                                    if (updates.rundowns.size > 0) {
                                        actions.push(
                                            RundownRecordActions.updateRundowns({
                                                records: [...updates.rundowns.values()],
                                            })
                                        );
                                    }

                                    // Display error message for failed deletions after all successful operations
                                    if (failedResults.length > 0) {
                                        const failedRecords = failedResults.map(result => `'${result.recordGuid}'- '${result.recordTitle}': ${result.error}`).join('\n');
                                        const errorMessage = languageSettings.PSTDeleteRecordsErrorMessage.replace('{failedRecords}', failedRecords);

                                        this.modalService.openAlert({ title: languageSettings.PSTDeleteRecordsErrorTitle, content: errorMessage, okString: languageSettings.PSTWebOkButtonLabel });
                                    }

                                    return actions.length > 0 ? actions : of(ApplicationActions.noOpAction());
                                })
                            );
                        })
                    );
            })
        );
    });

    openNewTemplateDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.openNewTemplateDialog),
            switchMap(action => {
                if (action.record?.Type === ObjectTypes.RUNDOWN) {
                    const i18n = this.store.selectSignal(selectI18N)();
                    return this.modalService
                        .openModal({
                            title: i18n.PSTWebSaveAsTemplateButtonLabel,
                            content: i18n.PSTWebSaveAsTemplateDescription,
                            yesString: i18n.PSTWebSaveButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => RecordHttpActions.saveRundownTemplate({ record: action.record, layout: action.layout, isFullscreen: action.isFullscreen }))
                        );
                } else if (action.record?.Type === ObjectTypes.NOTE) {
                    return of(NotesActions.openCreateNoteTemplate());
                } else {
                    this.dialog.open(CreateNewTemplateComponent, {
                        panelClass: 'st-create-new-template',
                        width: '22rem',
                        height: '22rem',
                        hasBackdrop: true,
                        data: {
                            record: action.record,
                            isFullscreen: action.isFullscreen,
                        },
                    });
                    return of();
                }
            })
        );
    });

    saveRundownAsTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.saveRundownTemplate),
            switchMap(action => {
                const record = { ...action.record };
                const getRundownObs = (rundownRecord: StorytellingRundownRecord) => {
                    if (!action.isFullscreen) {
                        return from(this.rundownService.getRundown(rundownRecord?.Database, rundownRecord?.Path?.fullPath, rundownRecord?.Guid)).pipe(
                            filter(response => response?.status),
                            map(response => response?.data)
                        );
                    }
                    return of(record);
                };

                return getRundownObs(record).pipe(
                    switchMap(roRecord => {
                        const i18n = this.store.selectSignal(selectI18N);
                        const rundownTemplates = this.store.selectSignal(selectRundownTemplates);
                        const existingTemplate = rundownTemplates().find(rundown => rundown?.Title === roRecord?.Title);
                        const layout = action.layout ?? this.store.selectSignal(selectFullscreenRundownSelectedLayout)() ?? roRecord?.DefaultLayout ?? roRecord?.TemplateLayout;
                        if (existingTemplate) {
                            const actions = [];
                            let newTemplateGuid: string;
                            return this.modalService
                                .openModal({
                                    title: i18n().PSTWebTemplateAlreadyExists,
                                    content: i18n().PSTWebOverwriteTemplate,
                                })
                                .result.pipe(
                                    filter(res => res === ModalResponseCodes.OK),
                                    switchMap(() => from(this.rundownService.saveRundownTemplate(roRecord, layout))),
                                    filter(response => response.status),
                                    map(response => {
                                        newTemplateGuid = response.data.Guid;
                                        // When we are updating template we are actually creating a new template and deleting the old one,
                                        // this is because of how the back-end is structured, so we need to add the new template to the list and remove the old one from the list in order for the navigation to update with the new template and remove the old one.
                                        actions.push(ListActions.addListGuids({ listGuids: [newTemplateGuid] }));
                                        actions.push(RundownRecordActions.updateRundownTemplates({ templates: [response.data] }));
                                    }),
                                    switchMap(() =>
                                        this.basicContentService.deleteBasicContent({
                                            database: existingTemplate.Database,
                                            path: existingTemplate.Path?.fullPath,
                                            guid: existingTemplate.Guid,
                                            type: existingTemplate.Type,
                                        })
                                    ),
                                    filter(response => response.status),
                                    map(() => {
                                        const fullscreenGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                                        if (fullscreenGuid === existingTemplate.Guid) {
                                            // if the template that was just overwritten is currently open in fullscreen,
                                            // we want to open the new template in fullscreen after the old one is deleted,
                                            // because the old one is deleted immediately after the new one is created,
                                            // we can be sure that the new template will have a different guid than the old one,
                                            // so we can check if the fullscreen guid is the same as the old template guid and if it is,
                                            // we open the new template in fullscreen.
                                            actions.push(FullscreenActions.openRundownTemplate({ recordGuid: newTemplateGuid, recordType: ObjectTypes.RUNDOWN }));
                                            actions.push(ListActions.navigateObject({ nodeUuid: newTemplateGuid }));
                                        }
                                        actions.push(ListActions.removeListGuids({ listGuids: [existingTemplate.Guid] }));
                                        actions.push(RundownRecordActions.deleteRundownTemplates({ templates: [existingTemplate] }));
                                    }),
                                    switchMap(() => {
                                        return from(actions);
                                    })
                                );
                        } else {
                            return from(this.rundownService.saveRundownTemplate(roRecord, layout)).pipe(
                                switchMap(response => of(RundownRecordActions.updateRundownTemplates({ templates: [response.data] })))
                            );
                        }
                    })
                );
            })
        );
    });

    archiveRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RundownRecordActions.archiveRundown),
            concatLatestFrom(() => [this.store.select(selectCurrentViewState)]),
            mergeMap(([action, vs]) => {
                const languageSettings = this.store.selectSignal(selectI18N)();
                const title = action.isCopy ? languageSettings.PSTRundownMenuArchiveCopy : languageSettings.PSTRundownMenuArchiveRO;
                const content = (action.isCopy ? languageSettings.PSTWebArchiveRundownCopyDescription : languageSettings.PSTWebArchiveRundownDescription).replace('{name}', action.record.Title);
                return this.modalService.openModal({ title, content, yesString: languageSettings.PSTWebNoButtonLabel, noString: languageSettings.PSTWebYesButtonLabel }).result.pipe(
                    take(1),
                    exhaustMap(modalResponse => {
                        if (modalResponse !== ModalResponseCodes.OK) {
                            return of(RundownRecordActions.updateRundowns({ records: [{ ...action.record, isArchived: false }] }));
                        } else {
                            return this.rundownService
                                .archiveRundown({
                                    record: action.record,
                                    isCopy: action?.isCopy,
                                    isAboveBlackline: action?.isAboveBlackline,
                                })
                                .pipe(
                                    filter(archiveRes => archiveRes.status),
                                    mergeMap(() => {
                                        if (action.isCopy) {
                                            const successTitle = languageSettings.PSTWebArchiveRundownCopySuccessTitle;
                                            const successContent = languageSettings.PSTWebArchiveRundownCopySuccessDescription.replace('{name}', action.record.Title);
                                            return this.modalService.openAlert({ title: successTitle, content: successContent, okString: languageSettings.PSTWebOkButtonLabel }).result.pipe(
                                                filter(alertRes => alertRes === ModalResponseCodes.OK),
                                                mergeMap(() => [
                                                    LiveApiActions.openRundownPropertiesDialog({
                                                        record: action.record,
                                                        popUpTitle: languageSettings.PSTRundownMenuProperties,
                                                    }),
                                                    RecordHttpActions.loadRundowns({ vs }),
                                                    LiveApiActions.reloadRundowns({
                                                        sourceROs: [
                                                            {
                                                                Guid: action.record?.Guid,
                                                                Database: action.record?.Database,
                                                                Path: action.record?.Path,
                                                            },
                                                        ],
                                                    }),
                                                ])
                                            );
                                        }
                                        return of(OpenFullscreenModalActions.closeFullscreenModal(), RecordHttpActions.loadRundowns({ vs }));
                                    }),
                                    catchError(() => {
                                        return of(RundownRecordActions.updateRundowns({ records: [{ ...action.record, isArchived: false }] }));
                                    })
                                );
                        }
                    })
                );
            }),
            catchError(() => of(ApplicationActions.noOpAction()))
        );
    });

    shouldProceedAttachment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.shouldProceedAttachment),
            switchMap(async action => {
                const files: AttachmentFiles[] = [];
                for (const attachment of action.recordAttachments) {
                    if (attachment) {
                        const shouldProceed = await this.attachmentService.shouldProceedAttachment(attachment.name, attachment.nativeFile.size);
                        if (shouldProceed) {
                            files.push({ file: attachment.nativeFile, finished: false, name: attachment.name });
                        }
                    }
                }
                return RecordHttpActions.createAttachment({ files: files, record: action.record });
            })
        );
    });

    fixMovedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.fixMovedStory),
            switchMap(action => {
                const record = action.record;
                const storyRecords = this.store.selectSignal(selectStoryRecordsMap)();
                const currentOpenedRecord = this.store.selectSignal(selectCurrentRecord)();
                let newStory: StorytellingStoryRecord = null;
                for (const story of record.children) {
                    if (story?.OriginalGUID) {
                        if (storyRecords.has(story?.OriginalGUID)) {
                            if (story?.OriginalGUID === currentOpenedRecord?.Guid) {
                                newStory = story;
                            }
                        }
                    }
                }

                if (newStory) {
                    return [
                        FullscreenActions.openStory({ recordGuid: newStory.Guid, recordType: ObjectTypes.SCRIPT }),
                        StoriesRecordActions.deleteStories({ records: [{ Guid: newStory.OriginalGUID }] }),
                    ];
                }
                return of(ApplicationActions.noOpAction());
            })
            // LEFT FOR REFERENCE
            // concatMap(story => {
            //     return from(this.basicContentService.getRecord<StorytellingStoryRecord>(story.Database, story.Path.fullPath, story.Guid)).pipe(
            //         switchMap(response => {
            //             if (response.status) {
            //                 return [StoriesRecordActions.updateStories({ records: [response.data] }), StoriesRecordActions.deleteStories({ records: [{ Guid: response.data.OriginalGUID }] })];
            //             }
            //         })
            //     );
            // })
        );
    });
    // TODO: Leaving this here for reference as we dont know what will happen after NAB
    // createAttachment$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(RecordHttpActions.createAttachment),
    //             switchMap(async action => {
    //                 const files = action.files;
    //                 const currentRecord = action.record;
    //                 let actions: Action[] = [];
    //                 if (files.length > 0) {
    //                     let baseFiles = [];
    //                     baseFiles = await this.appService.readUrl(files);

    //                     for (const file of baseFiles) {
    //                         const dialogResult = await firstValueFrom(this.appService.dialogService.open(AttachmentRenameComponent, { hasBackdrop: true, data: { fileName: file.Filename } }).closed);
    //                         if (dialogResult) {
    //                             file.name = dialogResult;
    //                             file.Filename = dialogResult;
    //                             file.Title = dialogResult;
    //                             file.ExternalFiles = currentRecord.ExternalFiles;
    //                             const res = await this.attachmentService.createAttachment(
    //                                 currentRecord.Database,
    //                                 currentRecord.Path.fullPath,
    //                                 currentRecord.Guid,
    //                                 false,
    //                                 file,
    //                                 currentRecord
    //                             );

    //                             if (res && res.status) {
    //                                 if (currentRecord.Type === ObjectTypes.TOPIC) {
    //                                     actions = [...actions, TopicRecordActions.updateTopics({ records: [currentRecord] })];
    //                                 } else {
    //                                     actions = [...actions, AssignmentRecordActions.updateAssignments({ records: [currentRecord] })];
    //                                 }
    //                             }
    //                         }
    //                     }
    //                     return actions;
    //                 }
    //             })
    //         );
    //     },
    //     { dispatch: false }
    // );

    createAttachment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.createAttachment),
            switchMap(action => {
                const files = action.files;
                const currentRecord = action.record;
                const actions: Action[] = [];
                return from(files).pipe(
                    concatMap(file => {
                        return this.appService.dialogService.open(AttachmentRenameComponent, { hasBackdrop: true, data: { fileName: file.name } }).closed.pipe(
                            exhaustMap(dialogResult => {
                                return from(this.attachmentService.createAttachment(dialogResult as string, file.file, currentRecord)).pipe(
                                    map(res => {
                                        if (res && res.status) {
                                            return res.data;
                                        }
                                    })
                                );
                            })
                        );
                    }),
                    reduce((acc, res) => {
                        acc.push(res);
                        return acc;
                    }, [] as StorytellingRecord[]),
                    switchMap(res => {
                        const { topics, assignments, stories } = res.reduce(
                            (acc, record) => {
                                switch (record.Type) {
                                    case ObjectTypes.TOPIC:
                                        acc.topics.push(record);
                                        break;
                                    case ObjectTypes.ASSIGNMENT:
                                        acc.assignments.push(record);
                                        break;
                                    case ObjectTypes.SCRIPT:
                                        acc.stories.push(record);
                                        break;
                                }
                                return acc;
                            },
                            { topics: [], assignments: [], stories: [] }
                        );

                        if (topics.length > 0) {
                            actions.push(TopicRecordActions.updateTopics({ records: topics }));
                        }
                        if (assignments.length > 0) {
                            actions.push(AssignmentRecordActions.updateAssignments({ records: assignments }));
                        }
                        if (stories.length > 0) {
                            actions.push(StoriesRecordActions.updateStories({ records: stories }));
                        }
                        return actions;
                    })
                );
            })
        );
    });

    $deleteAttachment = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteAttachmentPoolItem),

            exhaustMap(action => {
                const languageSettings = this.store.selectSignal(selectI18N)();
                const dialogMessage = languageSettings.PSTWebAttachmentDeleteModalMessage.replace('{attachmentName}', action.attachmentPoolItem.slugText);
                return this.modalService
                    .openModal({
                        title: languageSettings.PSTWebAttachmentDeleteModalTitle,
                        content: dialogMessage,
                    })
                    .result.pipe(
                        take(1),
                        filter(result => result === ModalResponseCodes.OK),
                        map(() => action)
                    );
            }),
            switchMap(action => {
                let record = action.attachmentPoolItem.parent;

                switch (record?.Type) {
                    case ObjectTypes.ASSIGNMENT: {
                        const assignments = this.store.selectSignal(selectAssignmentRecordsMap)();
                        record = assignments.get(record.Guid);

                        let mirItems = extractAttachments(record);
                        mirItems = mirItems.filter(item => {
                            return item.objId !== action.attachmentPoolItem.objId;
                        });

                        const value = mirItems.map(item => `[${item.mosItem}]`).join('~C13~~C10~');

                        return of(
                            RecordHttpActions.saveRecordChanges({
                                record: record,
                                fieldsToUpdate: [
                                    {
                                        key: PSTFieldNames.PSTDraftText,
                                        value: value,
                                    },
                                ],
                            })
                        );
                    }
                    case ObjectTypes.SCRIPT: {
                        const stories = this.store.selectSignal(selectStoryRecordsMap)();
                        record = stories.get(record.Guid);

                        let mirItems = extractAttachments(record);
                        mirItems = mirItems.filter(item => {
                            return item.objId !== action.attachmentPoolItem.objId;
                        });
                        const valueItems = mirItems.map(
                            item =>
                                `<div contenteditable="false" draggable="true" class="st-rich-text-editor--mos" mosclass="-${item.mosDeviceId}" mosid="${item.mosDeviceId}" id="${item.guid}">
                                    <div class="st-rich-text-editor--mos-transparent st-rich-text-editor--mos__content" id="${MOS_CONTENT_ID_PREFIX}${item.guid}">${encodeURIComponent(item.mosItem)}</div>
                                </div>`
                        );
                        const value = valueItems.join('');

                        return of(
                            RecordHttpActions.saveRecordChanges({
                                record: record,
                                fieldsToUpdate: [
                                    {
                                        key: PSTFieldNames.PSTDraftText,
                                        value: value,
                                    },
                                ],
                            })
                        );
                    }
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    // TODO: FIGURE OUT AFTER NAB
    // nativeAndroidUpload$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(RecordHttpActions.nativeAndroidUpload),
    //         switchMap(action => {
    //             const currentRecord = action.record;
    //             if (action.nativeAndroidEvent && action.nativeAndroidEvent.detail && action.nativeAndroidEvent.detail.file) {
    //                 const fileSize = Math.ceil(action.nativeAndroidEvent.detail.file.length / 4) * 3;
    //                 let actions: Action[] = [];
    //                 return from(this.attachmentService.shouldProceedAttachment(action.nativeAndroidEvent.detail.fileName, fileSize)).pipe(
    //                     switchMap(shouldProceed => {
    //                         if (shouldProceed) {
    //                             const file: any = {
    //                                 Type: ObjectTypes.EXTERNAL,
    //                                 FileBytes: action.nativeAndroidEvent.detail.file,
    //                                 Title: action.nativeAndroidEvent.detail.fileName,
    //                                 Filename: action.nativeAndroidEvent.detail.fileName,
    //                             };
    //                             file.ExternalFiles = currentRecord.ExternalFiles;
    //                             return from(this.attachmentService.createAttachment(currentRecord.Database, currentRecord.Path.fullPath, currentRecord.Guid, false, file, currentRecord)).pipe(
    //                                 switchMap(res => {
    //                                     if (res && res.status) {
    //                                         if (currentRecord.Type === ObjectTypes.TOPIC) {
    //                                             actions = [...actions, TopicRecordActions.updateTopics({ records: [currentRecord] })];
    //                                         } else {
    //                                             actions = [...actions, AssignmentRecordActions.updateAssignments({ records: [currentRecord] })];
    //                                         }
    //                                     }
    //                                     return actions;
    //                                 })
    //                             );
    //                         }
    //                     })
    //                 );
    //             }
    //         })
    //     );
    // });

    // removeAttachment$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(RecordHttpActions.removeAttachment),
    //             switchMap(action => {
    //                 const currentRecord = action.record;
    //                 currentRecord.ExternalFiles.remove(action.attachment.guid);
    //                 return from(this.appService.attachmentService.deleteAttachment(currentRecord, action.attachment));
    //             })
    //         );
    //     },
    //     { dispatch: false }
    // );

    onDeleteRecordTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteRecordTemplate),
            switchMap(action => {
                const { guid, recordType } = action;
                let record: StorytellingRecord = null;
                let updateAction: (props: { templates: StorytellingRecord[] }) => Action = null;
                switch (recordType) {
                    case ObjectTypes.TOPIC:
                        record = this.store.selectSignal(selectTopicTemplatesMap)()?.get(guid);
                        updateAction = TopicRecordActions.updateTopicTemplate;
                        break;
                    case ObjectTypes.SCRIPT:
                        record = this.store.selectSignal(selectStoryTemplatesMap)()?.get(guid);
                        updateAction = StoriesRecordActions.updateStoryTemplates;
                        break;
                    case ObjectTypes.RUNDOWN:
                        record = this.store.selectSignal(selectRundownTemplatesMap)()?.get(guid);
                        updateAction = RundownRecordActions.updateRundownTemplates;
                        break;
                    case ObjectTypes.NOTE:
                        record = this.store.selectSignal(selectNoteTemplatesMap)()?.get(guid);
                        updateAction = NotesRecordActions.updateNoteTemplates;
                        break;
                }
                if (record) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    const modalContent = languageSettings.PSTWebConfirmDeletePart1 + record?.Title + languageSettings.PSTWebConfirmDeleteTemplatePart2;
                    return this.modalService
                        .openModal({
                            title: languageSettings.PSTWebConfirmDeleteLabel,
                            content: modalContent,
                        })
                        .result.pipe(
                            take(1),
                            exhaustMap(res => {
                                if (res !== ModalResponseCodes.OK) {
                                    return of(updateAction({ templates: [{ ...record, isDeleted: false }] }));
                                } else {
                                    return this.appService.basicContentService.deleteBasicContent({ database: record.Database, path: record.Path.fullPath, guid: record.Guid, type: record.Type }).pipe(
                                        filter(delRes => delRes.status),
                                        exhaustMap(() => {
                                            const actions = [];
                                            const isDeletedRecordTemplateOpenInMainCard = this.store.selectSignal(selectFullscreenRecordGuid)() === record?.Guid;
                                            if (isDeletedRecordTemplateOpenInMainCard) {
                                                actions.push(OpenFullscreenModalActions.closeFullscreenModal());
                                            }
                                            actions.push(updateAction({ templates: [{ ...record, isDeleted: true }] }));
                                            switch (recordType) {
                                                case ObjectTypes.TOPIC:
                                                    actions.push(PlaybookApiActions.loadTopicTemplates());
                                                    break;
                                                case ObjectTypes.SCRIPT:
                                                    actions.push(PlaybookApiActions.loadStoryTemplates());
                                                    break;
                                                case ObjectTypes.RUNDOWN:
                                                    actions.push(LiveApiActions.loadRundownTemplates());
                                                    break;
                                                case ObjectTypes.NOTE:
                                                    actions.push(PlaybookApiActions.loadNoteTemplates());
                                                    break;
                                            }
                                            return actions;
                                        })
                                    );
                                }
                            }),
                            catchError(() => {
                                if (updateAction != null) {
                                    return of(updateAction({ templates: [{ ...record, isDeleted: false }] }));
                                }
                                return of(ApplicationActions.noOpAction());
                            })
                        );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    openAttachToTopic$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(TopicRecordActions.openAttachToDialog, StoriesRecordActions.saveToTopic),
                exhaustMap(data => {
                    //Need to talk with Yanko and Ned about this:
                    //For now I assumed that we don't want to close fullscreen modal when opening attach to dialog
                    this.dialog.open(StItemAttachToDialogComponent, {
                        width: '78.375rem',
                        height: '37.5rem',
                        hasBackdrop: true,
                        disableClose: true,
                        data: data?.record,
                    });
                    return of(ApplicationActions.noOpAction());
                })
            );
        },
        { dispatch: false }
    );

    topicSearchTerm$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TopicRecordActions.topicSearchTerm),
            concatLatestFrom(() => this.store.select(selectTopicRecordsMap)),
            switchMap(([action, topics]) => {
                if (action.searchTerm?.trim()) {
                    return this.playbookService.getTopicBySearchTerm(action.searchTerm).pipe(
                        concatMap(data => {
                            const topicsMap = new Map<string, StorytellingTopicRecord>(topics);
                            const guids = new Set<string>();
                            data.forEach(topic => {
                                topicsMap.set(topic.Guid, topic);
                                guids.add(topic.Guid);
                            });
                            return [TopicRecordActions.setTopics({ records: topicsMap }), TopicRecordActions.loadTopicsSuccess({ topics: data }), TopicRecordActions.setAttachedTopicGuids({ guids })];
                        }),
                        catchError(error => of(WireApiActions.loadWiresError({ error })))
                    );
                }
                //todo move to records effects
                return of(TopicRecordActions.loadTopicsSuccess({ topics: new Map<string, StorytellingTopicRecord>() }));
            })
        );
    });

    loadTopicViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TopicRecordActions.loadTopicViewStates),
            switchMap(() => {
                return from(this.viewStateService.loadViewStateRecords(AppName.PLAYBOOK, TeamScope.GLOBALANDUSER)).pipe(
                    switchMap(({ data }) => {
                        return [TopicRecordActions.loadTopicViewStatesSuccess({ data }), TopicRecordActions.setCurrentTopicViewState({ viewState: data?.[0] })];
                    }),
                    catchError(error => of(WireApiActions.loadWiresError({ error })))
                );
            })
        );
    });

    loadTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TopicRecordActions.setCurrentTopicViewState, TopicRecordActions.reloadTopicsBasedOnCurrentViewState),
            concatLatestFrom(() => this.store.select(selectTopicRecordsMap)),
            switchMap(([action, topicRecordsMap]) => {
                if (action?.viewState?.Guid) {
                    return this.playbookSearchService.getPlaybookResultsObservable(action.viewState).pipe(
                        map(res => {
                            if (res.status) {
                                return res.data.topics;
                            }
                            return new Map<string, StorytellingTopicRecord>();
                        }),
                        switchMap(topics => {
                            const topicsMap = new Map<string, StorytellingTopicRecord>(topicRecordsMap);
                            const guids = new Set<string>();
                            topics.forEach(topic => {
                                topicsMap.set(topic.Guid, topic);
                                guids.add(topic.Guid);
                            });
                            return [TopicRecordActions.setTopics({ records: topicsMap }), TopicRecordActions.loadTopicsSuccess({ topics }), TopicRecordActions.setAttachedTopicGuids({ guids })];
                        }),
                        catchError(error => of(WireApiActions.loadWiresError({ error })))
                    );
                }
                return of(TopicRecordActions.loadTopicsSuccess({ topics: new Map<string, StorytellingTopicRecord>() }));
            })
        );
    });

    copyToClipboard$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RecordHttpActions.copyToClipboard),
                switchMap(action => {
                    const { record } = action;

                    switch (record.Type) {
                        case ObjectTypes.TOPIC:
                            this.appService.clipboard.copy(record, ClipboardTypes.Topic);
                            break;
                        case ObjectTypes.ASSIGNMENT:
                            this.appService.clipboard.copy(record, ClipboardTypes.Assignment);
                            break;
                        case ObjectTypes.SCRIPT:
                            this.appService.clipboard.copy(record, ClipboardTypes.Story);
                            break;
                        case ObjectTypes.NOTE:
                            this.appService.clipboard.copy(record, ClipboardTypes.Note);
                            break;
                        case ObjectTypes.RUNDOWN: {
                            const rundown = record as StorytellingRundownRecord;
                            return this.rundownService.getRundown(rundown.Database, rundown.Path.fullPath, rundown.Guid).pipe(
                                tap(res => {
                                    if (res.status && res.data) {
                                        this.appService.clipboard.copy(res.data, ClipboardTypes.Rundown);
                                    } else {
                                        this.appService.clipboard.copy(rundown, ClipboardTypes.Rundown);
                                    }
                                }),
                                map(() => null)
                            );
                        }
                    }
                    return of(null);
                })
            );
        },
        { dispatch: false }
    );

    createObjectPlaceholder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordActions.createObjectPlaceholder),
            concatMap(action => {
                const record = { ...action.record };
                if (!record || record?.Path?.isWasteBinPath) {
                    return of(ApplicationActions.noOpAction());
                }
                const dialogRef = this.dialog.open(ObjectPlaceholderCreateComponent, {
                    width: '20rem',
                    hasBackdrop: true,
                });
                return dialogRef.afterClosed().pipe(
                    take(1),
                    exhaustMap((res: { mosObjectPlaceholder: string }) => {
                        const objPlaceholderRegex = /^([^;]+);([^;]*);([^;]+);([^;]+)$/;
                        if (res?.mosObjectPlaceholder && objPlaceholderRegex.test(res.mosObjectPlaceholder)) {
                            const fieldDef: EnpsField = {
                                key: PSTFieldNames.ObjectPlaceholderCreate,
                                value: res.mosObjectPlaceholder,
                            };
                            return of(RecordHttpActions.saveRecordChanges({ record: record, fieldsToUpdate: [fieldDef] }));
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => {
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    restoreContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.restoreContent),
            concatMap(action => {
                const record = action.record;
                if (record) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    return this.modalService
                        .openModal({
                            title: languageSettings.PSTWebRestoreContentTitle,
                            content: languageSettings.PSTWebRestoreContentMessage.replace('{recordTitle}', record.Title),
                            yesString: languageSettings.PSTWebRestoreContentYes,
                            noString: languageSettings.PSTWebCancelOption,
                            autofocus: 'YesButton',
                        })
                        .result.pipe(
                            switchMap(res => {
                                if (res === ModalResponseCodes.OK) {
                                    return this.wastebinService.patchWastebinContentObservable({
                                        database: record.Database,
                                        path: record.Path.fullPath,
                                        guid: record.Guid,
                                    });
                                }
                                return of(null);
                            }),
                            switchMap(res => {
                                if (res?.data) {
                                    return [ListActions.removeListGuids({ listGuids: [action.record.Guid] })];
                                }
                                return of(ApplicationActions.noOpAction());
                            })
                        );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    createStoryFromNote$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.createStoryFromNote),
            switchMap(action => {
                const noteRecord = action.record;
                if (noteRecord) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    const topicRecords = this.store.selectSignal(selectTopicRecordsMap)();
                    const parentTopic = topicRecords.get(noteRecord.PSTAncestorGUID);
                    let destinationPath = noteRecord.Path.fullPath;
                    if (parentTopic) {
                        destinationPath = `${parentTopic.Path.fullPath}\\R_${parentTopic.Guid}`;
                    }
                    return this.playbookService
                        .copyENPSObjectObs({
                            sourceData: noteRecord,
                            destinationData: {
                                Database: noteRecord.Database,
                                Path: destinationPath,
                                Type: ObjectTypes.SCRIPT,
                            },
                            propertiesOverride: [
                                {
                                    // convert note text to ml text for story
                                    FieldName: PSTFieldNames.PSTDraftText,
                                    FieldValue: ML2HtmlUtils.convertDisplayHTMLToML2(noteRecord.RawContent),
                                },
                                {
                                    FieldName: PSTFieldNames.PSTStoryTopicGUID,
                                    FieldValue: noteRecord.PSTAncestorGUID,
                                },
                                {
                                    FieldName: PSTFieldNames.Title,
                                    FieldValue: languageSettings[I18nKeys.PSTStoryDefaultTitle],
                                },
                                {
                                    FieldName: PSTFieldNames.PSTStoryOutputType,
                                    FieldValue: StoryOutputType.GENERIC,
                                },
                            ],
                        })
                        .pipe(
                            switchMap(res => {
                                if (res.status) {
                                    return [StoriesRecordActions.updateStories({ records: [res.data] }), FullscreenActions.openStory({ recordGuid: res.data.Guid, recordType: ObjectTypes.SCRIPT })];
                                }
                                return of(ApplicationActions.noOpAction());
                            })
                        );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    copyAssignmentToTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.copyAssignmentToTopic),
            switchMap(action => {
                const assignmentRecord = { ...action.record };
                const targetTopicGuid = action.targetTopicGuid;
                if (assignmentRecord && targetTopicGuid) {
                    const topicRecords = this.store.selectSignal(selectTopicRecordsMap)();
                    const targetTopic = topicRecords.get(targetTopicGuid);
                    if (targetTopic) {
                        const destinationPath = `${targetTopic.Path.fullPath}\\R_${targetTopic.Guid}`;
                        assignmentRecord.Path = new Path(destinationPath);
                        assignmentRecord.ParentGuid = targetTopic.Guid;
                        return from(this.playbookService.duplicateAssignment(assignmentRecord, false)).pipe(
                            map(rec => rec.data),
                            switchMap(newRecord => {
                                const updatedTopic = { ...targetTopic };
                                if (!targetTopic.Sequence?.includes(newRecord.Guid)) {
                                    updatedTopic.Sequence = [...(updatedTopic.Sequence || []), newRecord.Guid];
                                }
                                // Add the new assignment to the topic's children collection
                                updatedTopic.children = [...(updatedTopic.children || []), newRecord];
                                return [TopicRecordActions.updateTopics({ records: [updatedTopic] }), AssignmentRecordActions.updateAssignments({ records: [newRecord] })];
                            })
                        );
                    }
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });
}
